/**
 * The pure half of a code pane's Files column (RFC 0354): which ref it lists,
 * the changed files as a tree, and Go to file's match. No React, no fetch, so
 * each rule is testable on its own.
 */

import { parseCodeId } from '../../services/code/codeFile'

/** One row of `/changed` (packages/cli vcs-changed.ts). */
export interface ChangedFile {
  path: string
  status: 'added' | 'modified' | 'deleted' | 'renamed'
  oldPath?: string
  added: number
  removed: number
  uncommitted: boolean
}

export interface ChangedList {
  ref: string
  base: string | null
  /** The commit the list counts from; a deleted file is read here. */
  from: string | null
  files: ChangedFile[]
  truncated: boolean
}

export type FilesMode = 'changed' | 'all'

/** The ref a code tab reads: '' for the working tree. Null for a non-code id. */
export function tabRef(docId: string | null | undefined): string | null {
  if (!docId) return null
  const parsed = parseCodeId(docId)
  return parsed ? (parsed.ref ?? '') : null
}

/**
 * A pick in the branch picker, and the tab ref it was made under (3a). The
 * pick stands until the active tab's ref changes, the same rule the sidebar's
 * Files section follows for a session (RFC 0277 S11).
 */
export interface RefPick {
  ref: string
  under: string
}

export function listedRef(active: string, pick: RefPick | null): string {
  return pick && pick.under === active ? pick.ref : active
}

export interface ChangedFolder {
  kind: 'dir'
  /** Every segment it stands for, joined: `packages / frontend` is two. */
  name: string
  /** The path of its last segment, unique in the tree. */
  path: string
  children: ChangedNode[]
}

export interface ChangedLeaf {
  kind: 'file'
  name: string
  file: ChangedFile
}

export type ChangedNode = ChangedFolder | ChangedLeaf

/**
 * The changed files as a folder tree, folders first, with a folder that holds
 * only one folder joined to it (`packages / frontend`), the way a pull request
 * draws it. Joining is what keeps a deep monorepo path one row, not five.
 */
export function changedTree(files: ReadonlyArray<ChangedFile>): ChangedNode[] {
  interface Dir {
    dirs: Map<string, Dir>
    files: ChangedLeaf[]
  }
  const root: Dir = { dirs: new Map(), files: [] }
  for (const file of files) {
    const parts = file.path.split('/')
    const name = parts.pop() ?? file.path
    let at = root
    for (const part of parts) {
      let next = at.dirs.get(part)
      if (!next) {
        next = { dirs: new Map(), files: [] }
        at.dirs.set(part, next)
      }
      at = next
    }
    at.files.push({ kind: 'file', name, file })
  }

  const build = (dir: Dir, prefix: string): ChangedNode[] => {
    const folders = [...dir.dirs.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([name, child]): ChangedFolder => {
        let label = name
        let path = prefix ? `${prefix}/${name}` : name
        let at = child
        while (at.files.length === 0 && at.dirs.size === 1) {
          const [[next, only]] = [...at.dirs.entries()]
          label = `${label} / ${next}`
          path = `${path}/${next}`
          at = only
        }
        return { kind: 'dir', name: label, path, children: build(at, path) }
      })
    const leaves = [...dir.files].sort((a, b) =>
      a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
    )
    return [...folders, ...leaves]
  }
  return build(root, '')
}

/** The folders above a path, shallowest first: `a/b/c.ts` is `a`, `a/b`. */
export function ancestors(path: string): string[] {
  const parts = path.split('/')
  parts.pop()
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'))
}

/**
 * How well `query` matches `path`, or null for no match. Characters must
 * appear in order, as in ⌘P. A match in the file name beats one spread over
 * the folders, a run of consecutive characters beats scattered ones, and a
 * shorter path wins a tie.
 */
export function matchScore(path: string, query: string): number | null {
  const q = query.toLowerCase().replace(/\s+/g, '')
  if (!q) return 0
  const p = path.toLowerCase()
  const nameAt = p.lastIndexOf('/') + 1
  let score = 0
  let from = 0
  let prev = -2
  for (const ch of q) {
    const at = p.indexOf(ch, from)
    if (at === -1) return null
    if (at === prev + 1) score += 3
    if (at >= nameAt) score += 2
    if (at === nameAt || p[at - 1] === '/' || p[at - 1] === '.') score += 2
    prev = at
    from = at + 1
  }
  // The whole query inside the name, as typed, is what people mean.
  if (p.slice(nameAt).includes(q)) score += 10
  return score - path.length / 100
}

/** The best `limit` paths for `query`, best first. */
export function searchPaths(
  paths: ReadonlyArray<string>,
  query: string,
  limit = 100,
): string[] {
  const scored: [string, number][] = []
  for (const path of paths) {
    const s = matchScore(path, query)
    if (s !== null) scored.push([path, s])
  }
  scored.sort((a, b) => b[1] - a[1])
  return scored.slice(0, limit).map(([p]) => p)
}
